import { useState } from "react";
import { Phone, Mail, Menu, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOOKING_URL } from "@/lib/api";

const NAV = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-us" },
  { label: "Areas", href: "#areas" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white">
      {/* Top utility bar */}
      <div className="bg-slate-900 text-white text-xs" data-testid="top-utility-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <a
              href="tel:5169198679"
              data-testid="utility-phone-link"
              className="inline-flex items-center gap-2 hover:text-amber-300 transition-colors"
            >
              <Phone className="h-3.5 w-3.5" />
              <span className="font-medium tracking-wide">516-919-8679</span>
            </a>
            <a
              href="mailto:tj.clinicalsolutions@gmail.com"
              data-testid="utility-email-link"
              className="hidden sm:inline-flex items-center gap-2 hover:text-amber-300 transition-colors"
            >
              <Mail className="h-3.5 w-3.5" />
              <span>tj.clinicalsolutions@gmail.com</span>
            </a>
          </div>
          <span className="hidden md:inline-flex items-center gap-2 text-amber-300 tracking-[0.18em] uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
            Flexible Scheduling · NJ · PA · NY
          </span>
        </div>
      </div>

      {/* Main nav */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="brand-logo" className="flex items-center gap-3 group">
            <img
              src="/tj-logo.png"
              alt="T&J Clinical Solutions logo"
              className="h-11 w-11 object-contain"
              width={44}
              height={44}
            />
            <span className="flex flex-col leading-tight">
              <span className="font-heading font-semibold text-slate-900 text-[15px] tracking-tight">
                T&amp;J Clinical Solutions
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Healthcare Support Services
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-slate-700 hover:text-brand-teal transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="ghost"
              data-testid="nav-call-now-button"
              className="hidden md:inline-flex text-slate-900 hover:text-brand-teal hover:bg-slate-50 rounded-sm"
            >
              <a href="tel:5169198679">
                <Phone className="h-4 w-4 mr-2" /> Call Now
              </a>
            </Button>
            <Button
              asChild
              data-testid="nav-book-service-button"
              className="hidden sm:inline-flex bg-brand-teal hover:bg-brand-teal-hover text-white rounded-sm font-semibold"
            >
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book a Service <ExternalLink className="h-3.5 w-3.5 ml-2" />
              </a>
            </Button>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen(!open)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 border border-slate-200 rounded-sm text-slate-700"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu-panel">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm font-medium text-slate-700 py-2 border-b border-slate-100"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-service-link"
                className="mt-2 inline-flex justify-center items-center gap-2 bg-brand-teal text-white py-3 rounded-sm font-semibold"
              >
                Book a Service <ExternalLink className="h-4 w-4" />
              </a>
              <a
                href="tel:5169198679"
                data-testid="mobile-call-now-link"
                className="inline-flex justify-center items-center gap-2 border border-slate-900 text-slate-900 py-3 rounded-sm font-semibold"
              >
                <Phone className="h-4 w-4" /> Call 516-919-8679
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
